import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useFormik } from 'formik';
import { useDispatch, useSelector } from 'react-redux';
import { debounce } from 'lodash';
import { Tag } from 'primereact/tag';
import * as Yup from 'yup';

import PFButton from '../shared/PFPrime/PFButton.js';
import { setGlobalFilters } from '../../redux/slices/globalFilters.slice.js';
import {
  checkUrlString,
  getLoginUserId,
  checkPermission,
  matchExactString,
} from '../../utils/Helpers.js';
import { getDefaultFilterList } from '../ProjectManagement/Scheduling/Reports/Reports.service.js';
import {
  createPayloadFromFormikValues,
  isAtLeastOneFieldSelected,
} from '../Admin/NewConfiguration/SchedularReportConfig/components/AddEditReport.model.js';
import apiService from '../../services/api.service.js';
import { URL_CONSTANTS } from '../../constants/urlConstants.js';
import permissions from '../../config/permissions.js';
import { checkDashboardGlobalFilterApplied } from '../../utils/global-filter.helper';
import { withLazyLoadWithoutFallback } from '../shared/LazyLoad/WithLazyLoad';

import {
  processFilters,
  doCustomFieldsMatchDefaultFilter,
  convertDefaultCustomerFormattingToFormikCustomerFormat,
} from './helpers/filter.helper.js';
import {
  allStore,
  getCustomerList,
  getCompanyListForFilter,
  getDefaultFilterValuesById,
  getTeamListForFilter,
  getWorkroomOptions,
} from './GlobalFilters.services/GlobalFilters.service.js';
import GlobalFiltersHeader from './GlobalFiltersHeader.js';

const GlobalFiltersDailog = withLazyLoadWithoutFallback(
  () => import('./GlobalFiltersDailog.jsx')
);
const GlobalFilters = () => {
  const dispatch = useDispatch();
  const [store, setStore] = useState([]);
  const [workroom, setWorkroom] = useState([]);
  const [defaultFiltersOptions, setDefaultFiltersOptions] = useState([]);
  const [defaultFiltersLoaded, setDefaultFiltersLoaded] = useState(false);
  const [globalFilterVisible, setGlobalFilterVisible] = useState(false);
  const [customerList, setCustomerList] = useState([]);
  const [companyList, setCompanyList] = useState([]);
  const [showBadge, setShowBadge] = useState(false);
  const globalFilters = useSelector(state => state.globalFilters);
  const { techniciansLimitList } = useSelector(
    state => state.techniciansLimitList
  );
  const { userCategoryList } = useSelector(state => state.userCategoryList);
  const { userSkillLevelList } = useSelector(state => state.userSkillLevelList);
  const { territoryList } = useSelector(state => state.territoryList);
  const { types } = useSelector(state => state.types);
  const { categories } = useSelector(state => state.categories);
  const { projectStores } = useSelector(state => state.projectStores);
  const { districts } = useSelector(state => state.districts);
  const { sourceStatusType } = useSelector(state => state.sourceStatusType);
  const { workrooms } = useSelector(state => state.workrooms);
  const { projectStatusType } = useSelector(state => state.projectStatusType);
  const { userTypes } = useSelector(state => state.userTypes);
  const currentPath = window?.location?.href;
  const isScheduler = checkUrlString(currentPath, '/scheduler');

  const isCalendar = checkUrlString(currentPath, '/calendar');
  const [dropdownError, setDropdownError] = useState();
  const [loading, setLoading] = useState(false);
  const toast = useRef();
  const [customMsg, setCustomMsg] = useState(false);
  const isDashboard = matchExactString(currentPath, '/dashboard');
  const iswidgetDetails = checkUrlString(currentPath, '/widget/details');
  const isMap =
    checkUrlString(currentPath, '/project/map') ||
    checkUrlString(currentPath, '/scheduler');
  const [teamData, setTeamData] = useState([]);
  const { isMapVisible } = useSelector(state => state.newScheduler);

  let isFetching = false;
  let latestQuery = null;
  let pendingQuery = null;
  const shouldDisableWork = isScheduler ? isMapVisible : isMap ? true : false;
  const shouldDisable = isScheduler
    ? !(isScheduler && isMapVisible)
    : isCalendar
      ? true
      : false;
  const shouldDisableClient = isScheduler ? isMapVisible : isMap ? true : true;

  // emptyGlobalFiltersObj is a function because calling this will always get a fresh object with latest value for globalFilters?.isDashboardApplicable
  const emptyGlobalFiltersObj = () => ({
    defaultFilterDropdown: {},
    customers: [],
    stores: [],
    projectTypes: [],
    projectCategories: [],
    projectWorkrooms: [],
    projectStatus: [],
    districtId: [],
    defaultFilter: {},
    techniciansVal: [],
    company: [],
    sourceStatus: [],
    userType: [],
    dateScheduledFilterStart: [],
    dateScheduledFilterEnd: [],
    dateCompletionFilterStart: [],
    dateCompletionFilterEnd: [],
    checkDateFromFilter: [],
    checkDateToFilter: [],
    dateSoldFromFilter: [],
    dateSoldToFilter: [],
    isDashboardApplicable: globalFilters?.isDashboardApplicable ?? 0,
    team: [],
    territory: [],
    userCategory: [],
    userSkillLevel: [],
  });

  const fetchCustomerList = async searchValue => {
    if (isFetching) {
      pendingQuery = searchValue;
      return;
    }
    isFetching = true;
    latestQuery = searchValue;

    try {
      const response = await getCustomerList(searchValue);
      if (response && Array.isArray(response)) {
        setCustomerList(
          response.map(convertDefaultCustomerFormattingToFormikCustomerFormat)
        );
      } else {
        console.error('Invalid response format or empty response');
        setCustomerList([]);
      }
    } catch (error) {
      console.error('Error fetching customer list:', error);
      setCustomerList([]);
    } finally {
      isFetching = false;
      if (pendingQuery !== null && pendingQuery !== latestQuery) {
        const nextQuery = pendingQuery;
        pendingQuery = null;
        fetchCustomerList(nextQuery);
      }
    }
  };

  const [hasCustomFilterView, hasCustomFilterRemove, hasCustomFilterAdd] =
    useMemo(() => {
      return [
        checkPermission(
          permissions?.userManagement?.userManagementCustomFilterView
        ),

        checkPermission(
          permissions?.userManagement?.userManagementCustomFilterRemove
        ),

        checkPermission(
          permissions?.userManagement?.userManagementCustomFilterAdd
        ),
      ];
    }, [localStorage.getItem('permissions')]);

  const fetchDefaultFilterList = async () => {
    const response = await getDefaultFilterList();
    const data = [...(response ?? [])];
    setDefaultFiltersOptions(data);
    setDefaultFiltersLoaded(true);
  };

  const handleFilterChange = async value => {
    let newWorkroom = [];
    let newStoreIds = [];
    let selectedDefaultFiltersValues = {};
    let newInstallerIds = [];
    let newInstallerUserIds = [];
    let updatedSelectedDefaultFilters = {};

    if (value?.filter_id) {
      const response = await getDefaultFilterValuesById(value?.filter_id);

      if (response?.selectedFilter?.workroomList?.length) {
        newWorkroom = response?.selectedFilter?.workroomList?.map(
          item => item?.label
        );
      }
      if (response?.selectedFilter?.storeList?.length) {
        newStoreIds = response?.selectedFilter?.storeList?.map(
          item => item?.store_id
        );
      }
      if (response?.selectedFilter?.technicianList?.length) {
        newInstallerIds = response?.selectedFilter?.technicianList?.map(
          item => item?.installer_id
        );
      }
      if (response?.selectedFilter?.technicianList?.length) {
        newInstallerUserIds = response?.selectedFilter?.technicianList?.map(
          item => item?.user_id
        );
      }
      selectedDefaultFiltersValues = response?.selectedFilter;

      const selectedDefaultFilters = processFilters(value);
      updatedSelectedDefaultFilters = {
        ...selectedDefaultFilters,
        dashboardWorkroom: newWorkroom,
        calendarStoreIds: newStoreIds,
        newInstallerIds: newInstallerIds,
        newInstallerUserIds: newInstallerUserIds,
        selectedDefaultFiltersValues: {
          key: value?.filter_name,
          value: selectedDefaultFiltersValues,
        },
      };

      // "Apply to Dashboard" state from API
      const isDashboardApplicable = response?.is_apply_to_dashboard ? 1 : 0;

      // Prefill custom filter fields from selected default filter
      const selectedValues = selectedDefaultFiltersValues || {};

      const formattedCustomersForFormik = selectedValues.customerList?.map(
        convertDefaultCustomerFormattingToFormikCustomerFormat
      );

      formik?.setValues({
        ...formik.values,
        defaultFilter: updatedSelectedDefaultFilters,
        defaultFilterDropdown: value,
        projectTypes: selectedValues.projectTypeList || [],
        projectCategories: selectedValues.projectCategoryList || [],
        stores: selectedValues.storeList || [],
        projectStatus: selectedValues.statusList || [],
        projectWorkrooms: selectedValues.workroomList || [],
        techniciansVal: selectedValues.technicianList || [],
        company: selectedValues.companyList || [],
        team: selectedValues.teamList || [],
        customers: formattedCustomersForFormik || [],
        dateScheduledFilterStart: selectedValues.dateScheduledFilterStart || [],
        dateScheduledFilterEnd: selectedValues.dateScheduledFilterEnd || [],
        dateCompletionFilterStart:
          selectedValues.dateCompletionFilterStart || [],
        dateCompletionFilterEnd: selectedValues.dateCompletionFilterEnd || [],
        checkDateFromFilter: selectedValues.checkDateFromFilter || [],
        checkDateToFilter: selectedValues.checkDateToFilter || [],
        dateSoldFromFilter: selectedValues.dateSoldFromFilter || [],
        dateSoldToFilter: selectedValues.dateSoldToFilter || [],
        isDashboardApplicable,
        territory: selectedValues.territoryList || [],
        userCategory: selectedValues.userCategoryList || [],
        userSkillLevel: selectedValues.userSkillLevelList || [],
      });

      return {
        defaultFilter: updatedSelectedDefaultFilters,
        defaultFilterDropdown: value,
        isDashboardApplicable,
      };
    } else {
      formik?.setValues(emptyGlobalFiltersObj());
      return emptyGlobalFiltersObj();
    }
  };

  const formik = useFormik({
    initialValues: emptyGlobalFiltersObj(),
    onSubmit: values => {
      let payload = { ...values };

      if (
        values.defaultFilterDropdown?.filter_id &&
        doCustomFieldsMatchDefaultFilter(values)
      ) {
        // It's a default filter, clear custom fields
        payload = {
          ...payload,
          customers: [],
          stores: [],
          projectTypes: [],
          projectCategories: [],
          projectWorkrooms: [],
          projectStatus: [],
          districtId: [],
          techniciansVal: [],
          company: [],
          sourceStatus: [],
          userType: [],
          dateScheduledFilterStart: [],
          dateScheduledFilterEnd: [],
          dateCompletionFilterStart: [],
          dateCompletionFilterEnd: [],
          checkDateFromFilter: [],
          checkDateToFilter: [],
          dateSoldFromFilter: [],
          dateSoldToFilter: [],
          team: [],
          territory: [],
          userCategory: [],
          userSkillLevel: [],
        };
      } else {
        // It's a custom filter, clear default filter fields
        payload = {
          ...payload,
          defaultFilter: {},
          defaultFilterDropdown: {},
        };
      }

      localStorage.setItem('common_filters', JSON.stringify(payload));
      const keysWithValues = Object.keys(values).filter(
        key => Array.isArray(values[key]) && values[key].length > 0
      );

      const isDefaultFilterApplied =
        values.defaultFilterDropdown?.filter_id &&
        Object.keys(values.defaultFilter || {}).length > 0;
      const isCustomFilterApplied = keysWithValues?.length > 0;

      if (isDefaultFilterApplied || isCustomFilterApplied) {
        setShowBadge(true);
      } else {
        setShowBadge(false);
      }

      dispatch(
        setGlobalFilters({
          ...payload,
          changedAt: new Date(),
        })
      );
    },
  });

  useEffect(() => {
    fetchDefaultFilterList();
    fetchCustomerList();
    getTeamListForFilter().then(setTeamData);
    getCompanyListForFilter().then(setCompanyList);
  }, []);

  useEffect(() => {
    if (!defaultFiltersLoaded) return;
    const savedFilters = JSON.parse(localStorage.getItem('common_filters'));
    if (savedFilters) {
      // checking if its a filter amongst the default filters
      if (
        savedFilters.defaultFilterDropdown?.filter_id &&
        Object.keys(savedFilters.defaultFilter || {}).length > 0
      ) {
        // setting formik values here properly (default + custom(copied from default))
        formik.setValues({
          ...formik.values,
          defaultFilter: savedFilters.defaultFilter,
          defaultFilterDropdown: savedFilters.defaultFilterDropdown,
          projectTypes:
            savedFilters.defaultFilter.selectedDefaultFiltersValues?.value
              ?.projectTypeList || [],
          projectCategories:
            savedFilters.defaultFilter.selectedDefaultFiltersValues?.value
              ?.projectCategoryList || [],
          stores:
            savedFilters.defaultFilter.selectedDefaultFiltersValues?.value
              ?.storeList || [],
          projectStatus:
            savedFilters.defaultFilter.selectedDefaultFiltersValues?.value
              ?.statusList || [],
          projectWorkrooms:
            savedFilters.defaultFilter.selectedDefaultFiltersValues?.value
              ?.workroomList || [],
          techniciansVal:
            savedFilters.defaultFilter.selectedDefaultFiltersValues?.value
              ?.technicianList || [],
          company:
            savedFilters.defaultFilter.selectedDefaultFiltersValues?.value
              ?.companyList || [],
          team:
            savedFilters.defaultFilter.selectedDefaultFiltersValues?.value
              ?.teamList || [],
          customers:
            savedFilters.defaultFilter.selectedDefaultFiltersValues?.value?.customerList?.map(
              convertDefaultCustomerFormattingToFormikCustomerFormat
            ) || [],
          districtId:
            savedFilters.defaultFilter.selectedDefaultFiltersValues?.value
              ?.districtList || [],
          sourceStatus:
            savedFilters.defaultFilter.selectedDefaultFiltersValues?.value
              ?.sourceStatusList || [],
          isDashboardApplicable: savedFilters.isDashboardApplicable,
          territory:
            savedFilters.defaultFilter.selectedDefaultFiltersValues?.value
              ?.territoryList || [],
          userCategory:
            savedFilters.defaultFilter.selectedDefaultFiltersValues?.value?.userCategoryList?.map(
              item => ({
                user_category_id: item.user_category_id,
                user_category: item.user_category,
              })
            ) || [],
          userSkillLevel:
            savedFilters.defaultFilter.selectedDefaultFiltersValues?.value?.userSkillLevelList?.map(
              item => ({
                user_skill_level_id: item.user_skill_level_id,
                skill_level: item.skill_level,
              })
            ) || [],
        });
      } else {
        formik.setValues(savedFilters);
      }
      dispatch(setGlobalFilters(savedFilters));
      const keysWithValues = Object.keys(savedFilters).filter(
        key => Array.isArray(savedFilters[key]) && savedFilters[key].length > 0
      );
      const isDefaultFilterApplied =
        savedFilters.defaultFilterDropdown?.filter_id &&
        Object.keys(savedFilters.defaultFilter || {}).length > 0;
      const isCustomFilterApplied = keysWithValues?.length > 0;
      if (isDefaultFilterApplied || isCustomFilterApplied) {
        setShowBadge(true);
      } else {
        setShowBadge(false);
      }
    } else {
      // No saved filters, auto-apply default filter if present
      applyDefaultFilterIfPresent();
    }
  }, [defaultFiltersLoaded]);

  const applyDefaultFilterIfPresent = async () => {
    const byDefaultFilterValue = defaultFiltersOptions?.find(item =>
      item?.userFilterInfo
        ? item?.is_default && item?.userFilterInfo
        : item?.is_default
    );
    if (byDefaultFilterValue) {
      const payload = await handleFilterChange(byDefaultFilterValue);
      localStorage.setItem(
        'common_filters',
        JSON.stringify({ ...emptyGlobalFiltersObj(), ...payload })
      );
      dispatch(setGlobalFilters(payload));
      setShowBadge(true);
    } else {
      // No default filter, save empty filters
      localStorage.setItem(
        'common_filters',
        JSON.stringify(emptyGlobalFiltersObj())
      );
      formik.setValues(emptyGlobalFiltersObj());
      dispatch(setGlobalFilters(emptyGlobalFiltersObj()));
      setShowBadge(false);
    }
  };

  const filterOptions = debounce(async event => {
    if (event?.filter?.length > 2) {
      const searchStore = await allStore(event.filter);
      setStore(searchStore);
    } else {
      setStore([]);
    }
  }, 300);

  const filterWorkroomOptions = debounce(async event => {
    if (event?.filter?.length > 2) {
      const searchWorkroom = await getWorkroomOptions(event.filter);
      setWorkroom(searchWorkroom);
    } else {
      setWorkroom([]);
    }
  }, 300);

  const filterCustomerNameOptions = debounce(async event => {
    if (event?.filter?.length > 2) {
      await fetchCustomerList(event?.filter);
    } else {
      await fetchCustomerList();
    }
  }, 300);

  const defaultFiltersTemplate = options => {
    return (
      <span className="flex align-items-center">
        <span>{options?.filter_name}</span>
        {options?.userFilterInfo ? (
          <span className="pl-2 text-xs">
            <Tag value="Custom"></Tag>
          </span>
        ) : (
          ''
        )}
        {options && options?.is_default ? (
          <span className="pl-2 text-xs">
            <Tag severity="success" value="Default"></Tag>
          </span>
        ) : (
          ''
        )}
      </span>
    );
  };
  const handleDateFilters = (value, name) => {
    formik?.setFieldValue(name, value ? [value] : []);
  };
  const globalFilterHeader = React.useRef(null);
  const globalFilterButtonRef = React.useRef(null);

  const isGlobalWithProjectNumberFilterApplied = useMemo(() => {
    if (isScheduler) {
      const path = window.location.pathname.split('/');
      return path.length > 1 && path[2] && showBadge;
    }
    return false;
  }, [window.location.pathname, showBadge]);

  useEffect(() => {
    if (
      (checkDashboardGlobalFilterApplied(globalFilters)?.isFilterApplied &&
        isDashboard) ||
      isGlobalWithProjectNumberFilterApplied
    ) {
      if (globalFilterHeader.current && globalFilterButtonRef.current) {
        setTimeout(() => {
          globalFilterHeader.current.show({
            currentTarget: globalFilterButtonRef.current,
          });
          setTimeout(() => {
            globalFilterHeader.current.hide();
          }, 3000);
        }, 2000);
      }
    }
  }, [globalFilters]);

  //Save the Filter
  const saveUserFilter = async value => {
    try {
      setLoading(true);
      const userId = getLoginUserId();
      const response = await apiService.post(
        apiService.formatUrl(
          URL_CONSTANTS.SCHEDULER_FILTERS.saveUserLevelFilter,
          userId
        ),
        value
      );
      if (response && response?.status) {
        toast.current.show({
          severity: 'success',
          summary: 'Success',
          detail: 'User Filter save successfully.',
        });

        fetchDefaultFilterList();
        customFilterFormik.handleReset();
        formik.handleReset();
      } else {
        toast.current?.show({
          severity: 'error',
          summary: response?.data
            ? `${response?.data}`
            : 'Unable to add record.',
        });
      }
    } catch (error) {
      toast.current.show({
        severity: 'error',
        detail: 'Unable to add record.',
      });
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const customFilterFormikValidation = Yup.object().shape({
    filter_name: Yup.string().required('Required'),
  });
  const customFilterFormik = useFormik({
    initialValues: {
      filter_name: '',
      isActive: false,
    },
    validationSchema: customFilterFormikValidation,
    enableReinitialize: true,
    onSubmit: values => {
      const payload = createPayloadFromFormikValues(values);
      payload.is_apply_to_dashboard = !!(
        formik.values?.isDashboardApplicable ?? false
      );

      if (
        formik?.values?.territory ||
        formik?.values?.techniciansVal ||
        formik?.values?.userCategory ||
        formik?.values?.userSkillLevel ||
        formik?.values?.userType ||
        formik?.values?.dateScheduledFilterStart ||
        formik?.values?.dateScheduledFilterEnd ||
        formik?.values?.checkDateFromFilter ||
        formik?.values?.checkDateToFilter ||
        formik?.values?.dateCompletionFilterStart ||
        formik?.values?.dateCompletionFilterEnd ||
        formik?.values?.dateSoldFromFilter ||
        formik?.values?.dateSoldToFilter
      ) {
        setCustomMsg(true);
      }
      if (!isAtLeastOneFieldSelected(customFilterFormik)) {
        setDropdownError(true);
      } else {
        setDropdownError(false);
        saveUserFilter(payload);
      }
    },
  });

  const assignFormikValues = () => {
    customFilterFormik.setValues({
      ...customFilterFormik.values,
      store: formik.values.stores?.map(val => val?.store_number),
      projectCategory: formik.values.projectCategories?.map(
        val => val?.project_category_id
      ),
      status: formik.values.projectStatus?.map(val => val?.status_id),
      sourceStatus: formik.values.sourceStatus?.map(val => val?.status_id),
      projectType: formik.values.projectTypes?.map(val => val?.project_type_id),
      district: formik.values.districtId,
      workroom: formik.values.projectWorkrooms?.map(val => val?.type_id),
      customer: formik.values.customers?.map(val => val?.id),
      technician: formik.values?.techniciansVal?.map(val => {
        return val?.user_id;
      }),
      company: formik.values?.company?.map(val => val?.company_id),
      teams: formik.values.team?.map(val => val?.team_id),
      territory: Array.isArray(formik.values.territory)
        ? formik.values.territory?.map(t => t.territory_id)
        : [],
      userCategory: Array.isArray(formik.values.userCategory)
        ? formik.values.userCategory.map(v => v.user_category_id)
        : [],
      userSkillLevel: Array.isArray(formik.values.userSkillLevel)
        ? formik.values.userSkillLevel.map(v => v.user_skill_level_id)
        : [],
    });
  };

  return (
    <>
      <div className="relative" ref={globalFilterButtonRef}>
        <PFButton
          size="small"
          icon="pi pi-filter"
          text
          onMouseEnter={e => globalFilterHeader.current.show(e)}
          className="focus:shadow-none w-2rem xl:w-3rem hover:bg-red-50"
          onClick={e => {
            setGlobalFilterVisible(true);
            globalFilterHeader.current.hide(e);
          }}
          pt={{
            icon: {
              className: 'text-red-400',
            },
          }}
        />
        {showBadge && (
          <span
            className="green-animated-dot absolute"
            style={{ top: '4px' }}
          ></span>
        )}
        <GlobalFiltersHeader
          globalFilterHeader={globalFilterHeader}
          formik={formik}
          customFilterFormik={customFilterFormik}
          fetchCustomerList={fetchCustomerList}
          applyDefaultFilterIfPresent={applyDefaultFilterIfPresent}
        />
      </div>
      {globalFilterVisible && (
        <GlobalFiltersDailog
          store={store}
          projectStores={projectStores}
          workroom={workroom}
          defaultFiltersOptions={defaultFiltersOptions}
          globalFilterVisible={globalFilterVisible}
          setGlobalFilterVisible={setGlobalFilterVisible}
          customerList={customerList}
          companyList={companyList}
          techniciansLimitList={techniciansLimitList}
          territoryList={territoryList}
          userCategoryList={userCategoryList}
          userSkillLevelList={userSkillLevelList}
          types={types}
          categories={categories}
          districts={districts}
          sourceStatusType={sourceStatusType}
          workrooms={workrooms}
          projectStatusType={projectStatusType}
          userTypes={userTypes}
          isScheduler={isScheduler}
          isCalendar={isCalendar}
          dropdownError={dropdownError}
          setDropdownError={setDropdownError}
          loading={loading}
          toast={toast}
          customMsg={customMsg}
          setCustomMsg={setCustomMsg}
          isDashboard={isDashboard}
          iswidgetDetails={iswidgetDetails}
          isMap={isMap}
          teamData={teamData}
          isMapVisible={isMapVisible}
          shouldDisableWork={shouldDisableWork}
          shouldDisable={shouldDisable}
          shouldDisableClient={shouldDisableClient}
          fetchCustomerList={fetchCustomerList}
          hasCustomFilterView={hasCustomFilterView}
          hasCustomFilterRemove={hasCustomFilterRemove}
          hasCustomFilterAdd={hasCustomFilterAdd}
          handleFilterChange={handleFilterChange}
          formik={formik}
          filterOptions={filterOptions}
          filterWorkroomOptions={filterWorkroomOptions}
          filterCustomerNameOptions={filterCustomerNameOptions}
          defaultFiltersTemplate={defaultFiltersTemplate}
          handleDateFilters={handleDateFilters}
          customFilterFormik={customFilterFormik}
          assignFormikValues={assignFormikValues}
          applyDefaultFilterIfPresent={applyDefaultFilterIfPresent}
        ></GlobalFiltersDailog>
      )}
    </>
  );
};

export default GlobalFilters;
